import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/* Non-content elements that must never be inerted by the modal scoping. */
const NON_RENDERED_TAGS = new Set(['SCRIPT', 'STYLE', 'LINK', 'META', 'TEMPLATE', 'NOSCRIPT']);

export class CartDrawerComponent extends Component {
  requiredRefs = ['panel', 'backdrop'];

  /** @type {Element|null} */
  #lastFocused = null;

  /** @type {HTMLElement[]} roots set inert while the drawer is open */
  #inertBackgrounds = [];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.refs.backdrop?.addEventListener('click', this.#onBackdropClick);
    document.addEventListener('keydown', this.#onKeyDown);
    document.addEventListener('focusin', this.#onFocusIn);
    document.addEventListener('site:navigation:start', this.#onNavigationStart);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.refs.backdrop?.removeEventListener('click', this.#onBackdropClick);
    document.removeEventListener('keydown', this.#onKeyDown);
    document.removeEventListener('focusin', this.#onFocusIn);
    document.removeEventListener('site:navigation:start', this.#onNavigationStart);
    /* Never leave the page background inert if the drawer is torn down
       while open (e.g. a morph fallback replaced the element). */
    this.#setBackgroundInert(false);
  }

  open() {
    this.#lastFocused = document.activeElement;
    this.classList.add('is-open');
    document.body.classList.add('cart-drawer-open');
    this.refs.panel.inert = false;
    this.#setBackgroundInert(true);

    /* Focus synchronously — a rAF here was the focus-loss bug: cart:update
       events that carry section HTML wipe the drawer content in the same
       task, so by the time the rAF ran, the close button was detached and
       focus fell to <body>. If content is replaced right after this call,
       cart-items-component's #replaceContent re-focuses the fresh close
       button (see restoreMorphFocus in cart-refresh.js). */
    this.#focusAnchor()?.focus({ preventScroll: true });
  }

  close({ restoreFocus = true } = {}) {
    /* Drop is-open first so the focusin guard does not fight the handoff
       of focus back to the page. */
    this.classList.remove('is-open');
    document.body.classList.remove('cart-drawer-open');
    this.#setBackgroundInert(false);
    this.refs.panel.inert = true;

    if (restoreFocus) {
      this.#lastFocused?.focus();
    }
    this.#lastFocused = null;
  }

  toggle() {
    if (this.classList.contains('is-open')) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * on:click delegation target for the close button inside the drawer.
   * @param {Event} _event - proxy event (unused)
   * @param {Object} _data - parsed URLSearchParams data (unused)
   */
  handleCloseClick() {
    this.close();
  }

  /**
   * on:click delegation target for the cart icon toggle button.
   * @param {Event} _event - proxy event (unused)
   * @param {Object} _data - parsed URLSearchParams data (unused)
   */
  handleToggleClick() {
    this.toggle();
  }

  #onCartUpdate = (event) => {
    /* Only auto-open when the merchant has set the auto-open attribute
       and the update came from a product form (not a qty change or remove). */
    if (!this.hasAttribute('auto-open')) return;

    const source = event.detail?.data?.source;
    if (source === 'product-form-component') {
      this.open();
    }
  };

  /* The drawer DOM persists across soft navigations; a popstate traversal
     would otherwise swap the page underneath an open drawer with the body
     still scroll-locked. Focus is not restored: the navigation runtime
     snapshots scroll position around this event. */
  #onNavigationStart = () => {
    if (this.classList.contains('is-open')) this.close({ restoreFocus: false });
  };

  #onBackdropClick = () => {
    this.close();
  };

  #onKeyDown = (event) => {
    if (!this.classList.contains('is-open')) return;

    if (event.key === 'Escape') {
      this.close();
      return;
    }

    if (event.key === 'Tab') {
      const focusables = [...this.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )].filter((el) => el.offsetParent !== null || el === document.activeElement);

      if (!focusables.length) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      /* Focus fell out of the panel (e.g. to <body> after a content morph):
         re-enter the loop instead of letting the browser tab into
         background chrome. */
      if (this.refs.panel && !this.refs.panel.contains(document.activeElement)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus({ preventScroll: true });
        return;
      }

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  };

  /* Modal containment backstop: background `inert` plus the Tab loop cover
     normal flows, but focus can still escape the open drawer (lost to
     <body>, re-entered on chrome inside the header island, or moved
     programmatically). Nothing outside the panel is interactive while the
     backdrop intercepts pointers, so whatever lands outside is pulled back
     to the close button. */
  #onFocusIn = (event) => {
    if (!this.classList.contains('is-open')) return;
    if (event.target instanceof Node && this.refs.panel?.contains(event.target)) return;
    this.#focusAnchor()?.focus({ preventScroll: true });
  };

  /* Fresh query on purpose: the refs cache may briefly be stale right
     after a content morph, and the focus anchor must never be a detached
     node (that silent no-op was the auto-open focus bug). */
  #focusAnchor() {
    const target = this.querySelector('[data-ref="closeButton"]') || this.refs.panel || this;
    return target instanceof HTMLElement && target.isConnected ? target : null;
  }

  /* While the modal drawer is open, everything it covers becomes inert:
     every direct <body> child except the drawer's own ancestor chain and
     non-content elements, and — inside the chrome island — every island
     child except the one hosting the drawer (structure per
     layout/theme.liquid). Complements the closed-state panel `inert`:
     open = background inert, closed = panel inert. */
  #setBackgroundInert(active) {
    if (!active) {
      for (const node of this.#inertBackgrounds) node.inert = false;
      this.#inertBackgrounds = [];
      return;
    }

    this.#inertBackgrounds = [];
    for (const node of document.body.children) {
      if (!(node instanceof HTMLElement)) continue;
      if (NON_RENDERED_TAGS.has(node.tagName)) continue;
      if (node === this) continue;

      /* The chrome island hosts the drawer, so it "contains" us — descend
         into it and inert its sections except the one hosting the drawer. */
      if (node.hasAttribute('data-chrome-island')) {
        for (const child of node.children) {
          if (!(child instanceof HTMLElement)) continue;
          if (child === this || child.contains(this)) continue;
          child.inert = true;
          this.#inertBackgrounds.push(child);
        }
        continue;
      }

      if (node.contains(this)) continue;
      node.inert = true;
      this.#inertBackgrounds.push(node);
    }
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
